import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { AlertCircle, Globe, Database, RefreshCw, Smartphone, Laptop, FileCheck, Chrome, Siren as Firefox, Monitor, Mail, Clock, ArrowRight, ShieldAlert, UploadCloud, Zap, BookOpen } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const TroubleshootingPage: React.FC = () => {
  const { t } = useTranslation('troubleshooting');
  const { lang } = useParams<{ lang: string }>();

  return (
    <>
      <Helmet>
        <title>Troubleshooting Guide - ShiftPic</title>
        <meta name="description" content="Find solutions to common issues when converting images with ShiftPic. Our troubleshooting guide helps you resolve problems quickly and easily." />
        <meta name="keywords" content="ShiftPic troubleshooting, image conversion help, conversion issues, file format problems, image converter support, image loading issues, image format compatibility, image size limits, browser compatibility, conversion errors" />
        <link rel="canonical" href={`https://shiftpic.com/${lang || 'en'}/troubleshooting/`} />
        <meta property="og:title" content="Troubleshooting Guide - ShiftPic" />
        <meta property="og:description" content="Find solutions to common issues when converting images with ShiftPic. Our troubleshooting guide helps you resolve problems quickly and easily." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://shiftpic.com/${lang || 'en'}/troubleshooting/`} />
        <meta property="og:image" content="https://shiftpic.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Troubleshooting Guide - ShiftPic" />
        <meta name="twitter:description" content="Find solutions to common issues when converting images with ShiftPic. Our troubleshooting guide helps you resolve problems quickly and easily." />
        <meta name="twitter:image" content="https://shiftpic.com/og-image.jpg" />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="mt-8 mb-12">
            <h1>{t('title')}</h1>
            <p className="text-lg text-gray-600 max-w-3xl">{t('description')}</p>
          </div>

          <div className="mb-12 p-6 bg-amber-50 border-l-4 border-amber-400 rounded-r-lg">
            <h3 className="flex items-center gap-2 text-xl font-semibold text-amber-800 mb-4">
              <AlertCircle className="text-amber-500" size={24} aria-hidden="true" />
              {t('commonCauses.title')}
            </h3>
            <ul className="space-y-2 pl-7 list-disc text-amber-900">
              {Array.from({ length: 7 }).map((_, i) => (
                <li key={i} className="mb-2">{t(`commonCauses.list.${i}`)}</li>
              ))}
            </ul>
          </div>

          <h2 className="text-2xl font-bold mb-8">{t('steps.title')}</h2>
            
          <div className="grid grid-cols-1 md:grid-cols-1 gap-8 mb-12">
            {/* File Format Check */}
            <div className="bg-white p-6 rounded-card shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100">
              <h3 className="flex items-center gap-2 text-xl font-semibold mb-4">
                <Database className="text-primary" size={24} aria-hidden="true" />
                {t('steps.formats.title')}
              </h3>
              <p className="text-gray-700 mb-4">{t('steps.formats.description')}</p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <ul className="grid grid-cols-2 gap-y-2 list-none">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-1" />
                      <span className="text-sm">{t(`steps.formats.supportedFormats.${i}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <BookOpen size={16} className="text-primary-500" />
                <span>{t('steps.formats.checkTip')}</span>
              </div>
            </div>

            {/* Network Check */}
            <div className="bg-white p-6 rounded-card shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100">
              <h3 className="flex items-center gap-2 text-xl font-semibold mb-4">
                <Globe className="text-primary" size={24} aria-hidden="true" />
                {t('steps.network.title')}
              </h3>
              <p className="text-gray-700 mb-4">{t('steps.network.description')}</p>
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="flex items-start gap-3">
                  <Laptop className="text-primary-500 mt-0.5 flex-shrink-0" size={18} aria-hidden="true" />
                  <div>
                    <p className="font-medium text-gray-700">Desktop:</p>
                    <p className="text-sm text-gray-600">{t('steps.network.desktop')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Smartphone className="text-primary-500 mt-0.5 flex-shrink-0" size={18} aria-hidden="true" />
                  <div>
                    <p className="font-medium text-gray-700">Mobile:</p>
                    <p className="text-sm text-gray-600">{t('steps.network.mobile')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 mt-2">
                  <Zap className="text-primary-500 mt-0.5 flex-shrink-0" size={18} aria-hidden="true" />
                  <p className="text-sm text-gray-600">{t('steps.network.offlineMode')}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-1 gap-8 mb-12">
            {/* Clear Cache */}
            <div className="bg-white p-6 rounded-card shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100">
              <h3 className="flex items-center gap-2 text-xl font-semibold mb-4">
                <RefreshCw className="text-primary" size={24} aria-hidden="true" />
                {t('steps.cache.title')}
              </h3>
              <p className="text-gray-700 mb-4">{t('steps.cache.description')}</p>
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <Chrome className="text-primary-500 mr-2" size={20} />
                    <h4 className="font-semibold text-gray-800">Chrome</h4>
                  </div>
                  <ol className="list-decimal pl-6 space-y-1 text-sm text-gray-700">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <li key={i}>{t(`steps.cache.chrome.${i}`)}</li>
                    ))}
                  </ol>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <Firefox className="text-primary-500 mr-2" size={20} />
                    <h4 className="font-semibold text-gray-800">Firefox</h4>
                  </div>
                  <ol className="list-decimal pl-6 space-y-1 text-sm text-gray-700">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <li key={i}>{t(`steps.cache.firefox.${i}`)}</li>
                    ))}
                  </ol>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <Monitor className="text-primary-500 mr-2" size={20} />
                    <h4 className="font-semibold text-gray-800">Safari</h4>
                  </div>
                  <ol className="list-decimal pl-6 space-y-1 text-sm text-gray-700">
                    {Array.from({ length: 2 }).map((_, i) => (
                      <li key={i}>{t(`steps.cache.safari.${i}`)}</li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>

            {/* File Check - Verify Image Integrity */}
            <div className="bg-white p-6 rounded-card shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100">
              <h3 className="flex items-center gap-2 text-xl font-semibold mb-4">
                <FileCheck className="text-primary" size={24} aria-hidden="true" />
                {t('steps.fileCheck.title')}
              </h3>
              <p className="text-gray-700 mb-4">{t('steps.fileCheck.description')}</p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <ul className="space-y-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-primary-600 font-medium">{i + 1}</span>
                      </div>
                      <span className="text-sm text-gray-700">{t(`steps.fileCheck.list.${i}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-1 gap-8 mb-12">
            {/* Browser Update */}
            <div className="bg-white p-6 rounded-card shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100">
              <h3 className="flex items-center gap-2 text-xl font-semibold mb-4">
                <ShieldAlert className="text-primary" size={24} aria-hidden="true" />
                {t('steps.browserUpdate.title')}
              </h3>
              <p className="text-gray-700 mb-4">{t('steps.browserUpdate.description')}</p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <ul className="space-y-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-700 pb-2 border-b border-gray-200 last:border-0 last:pb-0">
                      <ArrowRight size={14} className="text-primary-500 flex-shrink-0" />
                      {t(`steps.browserUpdate.list.${i}`)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Alternatives - Alternative Methods */}
            <div className="bg-white p-6 rounded-card shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100">
              <h3 className="flex items-center gap-2 text-xl font-semibold mb-4">
                <UploadCloud className="text-primary" size={24} aria-hidden="true" />
                {t('steps.alternatives.title')}
              </h3>
              <p className="text-gray-700 mb-4">{t('steps.alternatives.description')}</p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <ul className="space-y-3">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-primary-600 font-medium">{i + 1}</span>
                      </div>
                      <span className="text-sm text-gray-700">{t(`steps.alternatives.list.${i}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          
          {/* Contact Section */}
          <div className="bg-white p-6 rounded-card shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 mb-8">
            <h3 className="flex items-center gap-2 text-xl font-semibold mb-4">
              <Mail className="text-primary" size={24} aria-hidden="true" />
              {t('contact.title')}
            </h3>
            <p className="text-gray-700 mb-4">{t('contact.description')}</p>
            <div className="bg-gray-50 p-4 rounded-lg flex flex-col space-y-3">
              <div className="flex items-start gap-3">
                <Mail className="text-primary-500 mt-0.5 flex-shrink-0" size={18} aria-hidden="true" />
                <p className="text-gray-700 font-medium">{t('contact.supportEmail')}</p>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="text-primary-500 mt-0.5 flex-shrink-0" size={18} aria-hidden="true" />
                <p className="text-sm text-gray-600">{t('contact.responseTime')}</p>
              </div>
            </div>
          </div>
        </div>
    </>
  );
};

export default TroubleshootingPage;